import axios from 'axios';

import type { BulkLoadResponse } from '../types/bulkload-response';
import type { AsyncTaskErrorEnvelope } from '../types/common';
import type { SellerFreightProvider } from '../types/freight-providers';
import type {
  BookFreightFulfillmentPlanRequest,
  BulkPrintFreightDocumentsRequest,
  CancelFreightFulfillmentPlanRequest,
  CustomExtractExportFreightRequest,
  FreightCalculateDensityRequest,
  FreightCommodityPresetGridRequest,
  FreightFulfillmentPlanExportRequest,
  FreightFulfillmentPlanGridRequest,
  FreightFulfillmentPlanSaveQuoteRequest,
  FreightGridStatusCountsRequest,
  FreightHandlingUnitPresetGridRequest,
  FreightQuoteRequest,
  FreightShipmentGridRequest,
  FreightTrackShipmentRequest,
  SaveFreightConfigurationRequest,
  VoidFreightRequest,
} from '../types/freight-requests';
import type {
  FreightCalculateDensityResponse,
  FreightCommodityPresetGridResponse,
  FreightFulfillmentPlanCommodityPreset,
  FreightFulfillmentPlanGridResponse,
  FreightGridStatusCountsResponse,
  FreightFulfillmentPlanHandlingUnitPreset,
  FreightHandlingUnitPresetGridResponse,
  FreightShipment,
  FreightShipmentGridResponse,
  FreightTrackShipmentResponse,
  VoidFreightResponse,
} from '../types/freight-responses';
import type { LtlDefaults } from '../types/ship-from';

export const getQuotes = (payload: FreightQuoteRequest): Promise<AsyncTaskErrorEnvelope> => {
  return axios.post('/freight/quote', payload).then((r) => r.data);
};

export const calculateDensity = (payload: FreightCalculateDensityRequest): Promise<FreightCalculateDensityResponse> => {
  return axios.post('/freight/calculate-density', payload).then((r) => r.data);
};

export const quoteFulfillmentPlan = (fulfillmentPlanId: string): Promise<AsyncTaskErrorEnvelope> => {
  return axios.post(`/freight/${fulfillmentPlanId}/quote`).then((r) => r.data);
};

export const bookFulfillmentPlan = (payload: BookFreightFulfillmentPlanRequest): Promise<AsyncTaskErrorEnvelope> => {
  return axios.post('/freight/fulfillment-plan/book', payload).then((r) => r.data);
};

export const trackShipment = (payload: FreightTrackShipmentRequest): Promise<FreightTrackShipmentResponse> => {
  return axios.post('/freight/track', payload).then((r) => r.data);
};

export const bulkPrintFreightDocuments = (payload: BulkPrintFreightDocumentsRequest): Promise<string[]> => {
  return axios.post('/freight/document/async', payload).then((r) => r.data.jobGuids);
};

export const combinedFreightDocumentsAsync = (freightShipmentId: string): Promise<string[]> => {
  return axios.post('/freight/document/async/combined', { freightShipmentId }).then((r) => r.data.jobGuids);
};

export const cancelFulfillmentPlans = (payload: CancelFreightFulfillmentPlanRequest): Promise<void> => {
  return axios.post('/freight/fulfillment-plan/cancel', payload).then(() => undefined);
};

export const voidFreight = (payload: VoidFreightRequest): Promise<VoidFreightResponse> => {
  return axios.post('/freight/void', payload).then((r) => r.data);
};

export const getBookedShipment = (freightShipmentId: string): Promise<FreightShipment> => {
  return axios.get(`/freight/shipment/${freightShipmentId}`).then((r) => r.data);
};

export const getFreightFulfillmentPlans = (payload: FreightFulfillmentPlanGridRequest): Promise<FreightFulfillmentPlanGridResponse> => {
  return axios.post('/freightfulfillmentplangrid', payload).then((r) => r.data);
};

/** Shipment-keyed sibling of {@link getFreightFulfillmentPlans}, backing the LTL Voided view. */
export const getFreightShipmentGrid = (payload: FreightShipmentGridRequest): Promise<FreightShipmentGridResponse> => {
  return axios.post('/FreightShipmentGrid', payload).then((r) => r.data);
};

/** Per-status aggregate counts for the LTL quicksearch nav. */
export const getFreightGridStatusCounts = (payload: FreightGridStatusCountsRequest): Promise<FreightGridStatusCountsResponse> => {
  return axios.post('/FreightGridStatusCounts', payload).then((r) => r.data);
};

export const saveQuoteToFulfillmentPlan = (payload: FreightFulfillmentPlanSaveQuoteRequest): Promise<BulkLoadResponse> => {
  return axios.post('/freight/fulfillment-plan/save-quote', payload).then((r) => r.data);
};

export const saveConfiguration = (fulfillmentPlanId: string, request: SaveFreightConfigurationRequest): Promise<BulkLoadResponse> => {
  return axios.put(`/freight/${fulfillmentPlanId}/save-configuration`, request).then((r) => r.data);
};

export const exportFreightShipments = (payload: FreightFulfillmentPlanExportRequest): Promise<AsyncTaskErrorEnvelope> => {
  return axios.post('/freight/export', payload).then((r) => r.data);
};

export const exportFreightShipmentsCustomFormat = (payload: CustomExtractExportFreightRequest): Promise<AsyncTaskErrorEnvelope> => {
  return axios.post('/freight/export/custom-format', payload).then((r) => r.data);
};

export const getSellerProviders = (): Promise<SellerFreightProvider[]> => {
  return axios.get<SellerFreightProvider[]>('/freight/seller-provider').then((r) => r.data);
};

export const updateSellerProviderNickname = (sellerFreightProviderId: number, nickname: string): Promise<void> => {
  return axios.patch(`/freight/seller-provider/${sellerFreightProviderId}/nickname`, { nickname }).then(() => undefined);
};

export const getFreightFulfillmentPlanCommodityPresets = (
  search: string,
  signal?: AbortSignal,
): Promise<FreightFulfillmentPlanCommodityPreset[]> => {
  return axios
    .get<FreightFulfillmentPlanCommodityPreset[]>('/freight/commodity-preset', { params: { search }, signal })
    .then((r) => r.data ?? []);
};

export const getFreightCommodityPresetGrid = (payload: FreightCommodityPresetGridRequest): Promise<FreightCommodityPresetGridResponse> =>
  axios.post<FreightCommodityPresetGridResponse>('/freight/commodity-preset/grid', payload).then((r) => r.data);

export const getFreightCommodityPreset = (commodityPresetId: number): Promise<FreightFulfillmentPlanCommodityPreset> =>
  axios.get<FreightFulfillmentPlanCommodityPreset>(`/freight/commodity-preset/${commodityPresetId}`).then((r) => r.data);

export const createFreightCommodityPreset = (
  payload: FreightFulfillmentPlanCommodityPreset,
): Promise<FreightFulfillmentPlanCommodityPreset> =>
  axios.post<FreightFulfillmentPlanCommodityPreset>('/freight/commodity-preset', payload).then((r) => r.data);

export const updateFreightCommodityPreset = (
  commodityPresetId: number,
  payload: FreightFulfillmentPlanCommodityPreset,
): Promise<FreightFulfillmentPlanCommodityPreset> =>
  axios.put<FreightFulfillmentPlanCommodityPreset>(`/freight/commodity-preset/${commodityPresetId}`, payload).then((r) => r.data);

export const deleteFreightCommodityPreset = (commodityPresetId: number): Promise<void> =>
  axios.delete(`/freight/commodity-preset/${commodityPresetId}`).then(() => undefined);

/** Saves LTL (freight) defaults for a ship-from location. */
export const saveShipFromLtlDefaults = (shipFromId: string, payload: LtlDefaults): Promise<void> =>
  axios.put(`/seller/shipfrom/${shipFromId}/ltl-defaults`, payload).then(() => undefined);

export const getFreightFulfillmentPlanHandlingUnitPresets = (
  search: string,
  signal?: AbortSignal,
): Promise<FreightFulfillmentPlanHandlingUnitPreset[]> => {
  return axios
    .get<FreightFulfillmentPlanHandlingUnitPreset[]>('/freight/handling-unit-preset', { params: { search }, signal })
    .then((r) => r.data ?? []);
};

export const getFreightHandlingUnitPresetGrid = (
  payload: FreightHandlingUnitPresetGridRequest,
): Promise<FreightHandlingUnitPresetGridResponse> =>
  axios.post<FreightHandlingUnitPresetGridResponse>('/freight/handling-unit-preset/grid', payload).then((r) => r.data);

export const getFreightHandlingUnitPreset = (handlingUnitPresetId: number): Promise<FreightFulfillmentPlanHandlingUnitPreset> =>
  axios.get<FreightFulfillmentPlanHandlingUnitPreset>(`/freight/handling-unit-preset/${handlingUnitPresetId}`).then((r) => r.data);

export const createFreightHandlingUnitPreset = (
  payload: FreightFulfillmentPlanHandlingUnitPreset,
): Promise<FreightFulfillmentPlanHandlingUnitPreset> =>
  axios.post<FreightFulfillmentPlanHandlingUnitPreset>('/freight/handling-unit-preset', payload).then((r) => r.data);

export const updateFreightHandlingUnitPreset = (
  handlingUnitPresetId: number,
  payload: FreightFulfillmentPlanHandlingUnitPreset,
): Promise<FreightFulfillmentPlanHandlingUnitPreset> =>
  axios.put<FreightFulfillmentPlanHandlingUnitPreset>(`/freight/handling-unit-preset/${handlingUnitPresetId}`, payload).then((r) => r.data);

export const deleteFreightHandlingUnitPreset = (handlingUnitPresetId: number): Promise<void> =>
  axios.delete(`/freight/handling-unit-preset/${handlingUnitPresetId}`).then(() => undefined);

export const emailFreightDocument = (freightShipmentId: string, emailAddress: string, documentTypes?: string[]): Promise<void> =>
  axios.post(`/freight/shipment/${freightShipmentId}/email-document`, { emailAddress, documentTypes }).then(() => undefined);

export const emailFreightDocumentByUrl = (freightShipmentId: string, emailAddress: string, documentUrl: string): Promise<void> =>
  axios.post(`/freight/shipment/${freightShipmentId}/email-document`, { emailAddress, documentUrl }).then(() => undefined);
